import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsContent } from '@/components/ui/tabs';
import { PieChart, Pie, Cell, Treemap, Tooltip, ResponsiveContainer } from 'recharts';
import { CHART_COLORS } from './colors';

/**
 * Extracted from CategoryConsole.js (component-split refactor, round 4).
 * Overview tab: category spend treemap, L1 pie chart, and the L2 spend table.
 * Note: the L2 rows are rendered through a JSX fragment inside <tbody> on
 * purpose — the visual-edits babel plugin wraps expression children of native
 * elements in a <span>, which is invalid inside <tbody> (hydration warning).
 */
export function OverviewTab({ data, cs }) {
  // Prepare treemap data
  const treemapData = data?.categories?.slice(0, 10).map(cat => ({
    name: cat.category,
    size: cat.total_spend,
    spend: cat.total_spend
  })) || [];

  const categoryL2Rows = (data?.categories_l2 || []).slice(0, 15).map((cat) => (
    <tr key={cat.category} className="border-b hover:bg-muted/50 transition-colors">
      <td className="py-1.5 px-3 font-medium">{cat.category}</td>
      <td className="py-1.5 px-3 text-muted-foreground text-xs">{cat.parent}</td>
      <td className="py-1.5 px-3 text-right font-mono">{cs}{cat.total_spend?.toLocaleString()}</td>
      <td className="py-1.5 px-3 text-right">{cat.spend_percentage?.toFixed(1)}%</td>
      <td className="py-1.5 px-3 text-right">{cat.transaction_count}</td>
      <td className="py-1.5 px-3 text-right">{cat.supplier_count}</td>
    </tr>
  ));

  return (
    <TabsContent value="overview" className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Treemap */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card data-testid="category-treemap" className="h-full">
            <CardHeader>
              <CardTitle>Category Spend Treemap</CardTitle>
            </CardHeader>
            <CardContent>
              {treemapData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <Treemap
                    data={treemapData}
                    dataKey="size"
                    nameKey="name"
                    aspectRatio={4 / 3}
                    stroke="#fff"
                    isAnimationActive={false}
                    content={(props) => {
                      const { depth, x, y, width, height, index, name } = props;

                      // Only render for depth 1 with minimum size
                      if (depth !== 1 || width < 30 || height < 25) return null;

                      // Get category name - use index to lookup from treemapData
                      const cellData = treemapData[index];
                      const categoryName = cellData?.name || name || '';

                      return (
                        <g>
                          <rect
                            x={x}
                            y={y}
                            width={width}
                            height={height}
                            style={{
                              fill: CHART_COLORS[index % CHART_COLORS.length],
                              stroke: '#fff',
                              strokeWidth: 2,
                              cursor: 'pointer',
                            }}
                          />
                          <>{width > 50 && height > 30 && (
                            <text
                              x={x + width / 2}
                              y={y + height / 2}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill="#fff"
                              fontSize={11}
                              fontWeight="bold"
                            >
                              <>{categoryName.length > 12 ? categoryName.slice(0, 12) + '...' : categoryName}</>
                            </text>
                          )}</>
                        </g>
                      );
                    }}
                  >
                    <Tooltip
                      content={({ payload }) => {
                        if (payload && payload.length > 0) {
                          const item = payload[0];
                          const cellData = item?.payload;
                          const totalSpend = treemapData.reduce((sum, d) => sum + d.size, 0);
                          if (cellData) {
                            return (
                              <div className="bg-popover border rounded-lg shadow-lg p-3">
                                <p className="font-semibold">{cellData.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  Spend: {cs}{cellData.size?.toLocaleString()}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {((cellData.size / totalSpend) * 100).toFixed(1)}% of total
                                </p>
                              </div>
                            );
                          }
                        }
                        return null;
                      }}
                    />
                  </Treemap>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-8">No category data available</p>
              )}
              {/* Legend/Key */}
              {treemapData.length > 0 && (
                <div className="mt-4 pt-3 border-t">
                  <div className="flex flex-wrap gap-3 justify-center">
                    {treemapData.map((cat, index) => {
                      const valueInMillions = (cat.size / 1000000).toFixed(1);
                      return (
                        <div key={cat.name} className="flex items-center gap-1.5 text-xs">
                          <div
                            className="w-3 h-3 rounded-sm shrink-0"
                            style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                          />
                          <span className="text-muted-foreground">
                            {cat.name} ({cs}{valueInMillions}M)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Pie Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Spend by Category Level 1</CardTitle>
            </CardHeader>
            <CardContent>
              {data?.categories?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={data.categories.slice(0, 6)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        innerRadius={30}
                        fill="#8884d8"
                        dataKey="total_spend"
                        nameKey="category"
                      >
                        {data.categories.slice(0, 6).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${cs}${value.toLocaleString()}`, 'Spend']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-2">
                    {data.categories.slice(0, 6).map((cat, index) => (
                      <div key={cat.category} className="flex items-center gap-1.5 text-xs">
                        <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                        <span className="text-muted-foreground">{cat.category} ({cat.spend_percentage?.toFixed(0)}%)</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-center text-muted-foreground py-8">No category data available</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Category Table */}
      <Card>
        <CardHeader>
          <CardTitle>Spend by Category Level 2</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="category-l2-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Category</th>
                  <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Parent</th>
                  <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Spend</th>
                  <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">% Total</th>
                  <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Transactions</th>
                  <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Suppliers</th>
                </tr>
              </thead>
              <tbody>
                <>{categoryL2Rows}</>
              </tbody>
            </table>
            {(!data?.categories_l2 || data.categories_l2.length === 0) && (
              <p className="text-center text-muted-foreground py-8">No category data available</p>
            )}
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
}
